import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Mail, KeyRound, Link2, Send, AppWindow } from "lucide-react";
import { api } from "../lib/api";

function buildMailto({ to, subject, body }) {
  // encodeURIComponent turns spaces into %20 (proper), whereas URLSearchParams
  // uses `+`, which renders literally in some mail clients and looks unprofessional.
  const enc = (s) => encodeURIComponent(s || "");
  return `mailto:${enc(to)}?subject=${enc(subject)}&body=${enc(body)}`;
}

/**
 * InviteUserModal — admin sends an invitation email to a user.
 *   mode "link"        → one-time set-password link (safer, expires 24h)
 *   mode "credentials" → plain-text email + password (admin re-types the password)
 */
export default function InviteUserModal({ open, user, onClose }) {
  const [mode, setMode] = useState("link");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("link");
    }
  }, [open]);

  if (!open || !user) return null;

  const submit = async (e) => {
    e.preventDefault();
    await runSend({ send: true });
  };

  const openInMyEmail = async () => {
    await runSend({ send: false });
  };

  const runSend = async ({ send }) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/invite`, { mode, send });
      if (send) {
        toast.success(
          data.mode === "link"
            ? "Set-password link sent."
            : "Credentials email sent — a fresh password was generated and emailed."
        );
        onClose();
        return;
      }
      // Hand off to the OS default mail client.
      const to = data.recipient_email;
      const nameGreeting = data.recipient_name || to.split("@")[0];
      let subject;
      let body;
      if (data.mode === "link") {
        subject = "Your LingoFacil invitation";
        body =
          `Hi ${nameGreeting},\n\n` +
          `You've been invited to LingoFacil. Click the link below to set your password (expires in ${data.expires_hours} hours):\n\n` +
          `${data.set_password_url}\n\n` +
          `See you inside!`;
      } else {
        subject = "Your LingoFacil account is ready";
        body =
          `Hi ${nameGreeting},\n\n` +
          `Your LingoFacil account has been created. Use these credentials to sign in:\n\n` +
          `Email: ${to}\n` +
          `Password: ${data.password}\n\n` +
          `Sign-in URL: ${data.login_url}\n\n` +
          `Please change your password after your first sign-in from the Profile page.`;
      }
      window.location.href = buildMailto({ to, subject, body });
      toast.success("Opening your email app… hit Send there to deliver.");
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503) {
        toast.error(detail || "Email service not configured. Set RESEND_API_KEY, or use 'Open in my email app' instead.");
      } else {
        toast.error(detail || "Failed to prepare invitation");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="brutal-card p-6 bg-white max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-user-modal"
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <div className="brutal-badge bg-brand-mint text-xs mb-2">
              <Mail className="w-3.5 h-3.5" strokeWidth={3} /> Invitation
            </div>
            <h3 className="font-display text-2xl font-bold text-brand-navy">Invite by email</h3>
            <p className="text-sm text-brand-navy/60 mt-1">
              Sending to <span className="font-mono">{user.email}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-close"
            className="p-2 hover:bg-brand-cream rounded-full shrink-0"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <label
            className={`brutal-card p-4 flex items-start gap-3 cursor-pointer transition-all ${mode === "link" ? "bg-brand-yellow" : "bg-white hover:-translate-y-0.5"}`}
            data-testid="invite-mode-link"
          >
            <input
              type="radio"
              name="mode"
              value="link"
              checked={mode === "link"}
              onChange={() => setMode("link")}
              className="mt-1"
            />
            <div>
              <div className="font-bold text-brand-navy flex items-center gap-2">
                <Link2 className="w-4 h-4" strokeWidth={3} /> Set-password link (recommended)
              </div>
              <div className="text-xs text-brand-navy/70 mt-1">
                One-time link, expires in 24 hours. Password never leaves your Resend dashboard.
              </div>
            </div>
          </label>
          <label
            className={`brutal-card p-4 flex items-start gap-3 cursor-pointer transition-all ${mode === "credentials" ? "bg-brand-yellow" : "bg-white hover:-translate-y-0.5"}`}
            data-testid="invite-mode-credentials"
          >
            <input
              type="radio"
              name="mode"
              value="credentials"
              checked={mode === "credentials"}
              onChange={() => setMode("credentials")}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-bold text-brand-navy flex items-center gap-2">
                <KeyRound className="w-4 h-4" strokeWidth={3} /> Send credentials in plain text
              </div>
              <div className="text-xs text-brand-navy/70 mt-1">
                A fresh secure password is generated automatically and emailed with the login URL. The user's previous password (if any) is replaced.
              </div>
            </div>
          </label>
        </div>

        <div className="space-y-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="invite-submit"
            className="brutal-btn-pink w-full py-3"
          >
            {busy ? "Sending…" : (<><Send className="w-4 h-4" strokeWidth={3} /> Send via Resend</>)}
          </button>
          <button
            type="button"
            onClick={openInMyEmail}
            disabled={busy}
            data-testid="invite-open-mailto"
            className="brutal-btn-white w-full py-3"
            title="Open your default email app (Gmail / Outlook / Mail) with the invitation ready to send"
          >
            <AppWindow className="w-4 h-4" strokeWidth={3} /> Open in my email app
          </button>
          <p className="text-xs text-brand-navy/60 leading-relaxed pt-1">
            <strong>Send via Resend</strong> delivers automatically (needs Resend API key).<br />
            <strong>Open in my email app</strong> hands off to Gmail/Outlook/Apple Mail — no API key required.
          </p>
        </div>
      </form>
    </div>
  );
}
